import {
  POINT_STRIDE, PAGE_H, PAGE_W, bboxOfPoints, isHighlighter, profileOf, rowOfBBox, uuidv7,
  type BBox, type Paper, type Stroke,
} from 'shared';
import { SpatialIndex } from './spatialIndex.js';
import { drawPaper, type RowMarkKind } from './paper.js';
import { buildPathFor, forgetStroke, paintStroke, pathForStroke, pointsToTriples, sortForPainting } from './render.js';
import {
  markErasedPoints, piecesFromMask, strokeHitBySegment, strokeInLasso, strokeInRect, translatePoints,
} from './hitTest.js';
import type {
  DebugStats, EngineCallbacks, EngineMode, EngineOptions, EraserSettings,
  PenSettings, SelectSettings, Viewport,
} from './types.js';

const MAX_DPR = 2.5;
const UNDO_LIMIT = 200;
const PEN_GRACE_MS = 500; // janela em que o toque continua sendo palma
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

type UndoAction =
  | { type: 'add'; strokes: Stroke[] }
  | { type: 'delete'; strokes: Stroke[] };

interface TouchPoint { id: number; x: number; y: number }

export class InkEngine {
  private paperCanvas: HTMLCanvasElement;
  private inkCanvas: HTMLCanvasElement;
  private liveCanvas: HTMLCanvasElement;
  private paperCtx: CanvasRenderingContext2D;
  private inkCtx: CanvasRenderingContext2D;
  private liveCtx: CanvasRenderingContext2D;

  private index = new SpatialIndex();
  private viewport: Viewport = { scale: 1, tx: 0, ty: 0 };
  private fitScale = 1;
  private dpr = 1;

  private pen: PenSettings = { kind: 'ballpoint', color: '#15213A', size: 2.8, opacity: 1 };
  private mode: EngineMode = 'draw';
  private eraser: EraserSettings = { mode: 'stroke', size: 14, highlighterOnly: false };
  private select: SelectSettings = { mode: 'lasso' };
  private opts: EngineOptions;
  private cb: EngineCallbacks;

  private paper: Paper = 'ruled';
  private marks = new Map<number, RowMarkKind>();
  private highlightRows: number[] = [];

  // Estado da caneta
  private penDown = false;
  private penLeftAt = 0;
  private penHovering = false;
  private drawing: { id: number; pts: number[]; startedAt: number; pen: PenSettings } | null = null;
  private erasing: {
    id: number;
    last: [number, number];
    /** traços tocados, guardados inteiros até o gesto terminar */
    originals: Map<string, Stroke>;
    /** 1 = ponto removido. Só cresce, então o gesto não se realimenta */
    masks: Map<string, Uint8Array>;
  } | null = null;
  private lasso: { id: number; poly: number[]; rect: boolean } | null = null;
  private selection: string[] = [];
  private dragging: { id: number; from: [number, number]; dx: number; dy: number } | null = null;

  // Gestos com o dedo
  private touches = new Map<number, TouchPoint>();
  private gesture: { startViewport: Viewport; originX: number; originY: number; startDist: number; startCx: number; startCy: number } | null = null;

  private undoStack: UndoAction[] = [];
  private redoStack: UndoAction[] = [];

  private raf = 0;
  private stats: DebugStats = { fps: 0, latencyMs: 0, strokeCount: 0, lastFrameMs: 0 };
  private frameTimes: number[] = [];
  private lastEventTs = 0;
  private resizeObserver: ResizeObserver;
  private destroyed = false;
  private lowLatency = true;
  private opaqueLayerDetected = false;

  constructor(private container: HTMLElement, opts: EngineOptions, cb: EngineCallbacks) {
    this.opts = opts;
    this.cb = cb;
    container.style.position = 'relative';
    container.style.touchAction = 'none';
    container.style.overscrollBehavior = 'none';

    const make = (z: number, desync: boolean) => {
      const c = document.createElement('canvas');
      c.style.position = 'absolute';
      c.style.inset = '0';
      c.style.zIndex = String(z);
      c.style.transformOrigin = '0 0';
      c.style.touchAction = 'none';
      container.append(c);
      // `desynchronized` é o que dá a latência baixa da caneta, mas alguns
      // drivers Android recusam a opção ou devolvem uma camada OPACA — e,
      // como essa camada cobre a página inteira, o caderno vira uma tela
      // preta. Tentar e cair para o contexto comum é melhor do que insistir.
      let ctx: CanvasRenderingContext2D | null = null;
      if (desync) {
        try {
          ctx = c.getContext('2d', { desynchronized: true, alpha: true });
        } catch {
          ctx = null;
        }
      }
      if (!ctx) ctx = c.getContext('2d', { alpha: true });
      if (!ctx) throw new Error('Este navegador não fornece canvas 2D.');
      return [c, ctx] as const;
    };
    [this.paperCanvas, this.paperCtx] = make(0, false);
    [this.inkCanvas, this.inkCtx] = make(1, false);
    this.lowLatency = opts.lowLatencyInk !== false;
    [this.liveCanvas, this.liveCtx] = make(2, this.lowLatency);

    this.liveCanvas.style.pointerEvents = 'auto';
    this.attach(this.liveCanvas);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.checkLiveLayerIsTransparent();
  }

  /**
   * A camada do traço fica por cima de tudo. Se o driver devolver um canvas
   * opaco em vez de transparente, o usuário vê a tela toda preta e não tem
   * como adivinhar o motivo. Aqui isso é detectado e desligado sozinho.
   */
  private checkLiveLayerIsTransparent(): void {
    if (!this.lowLatency) return;
    try {
      if (!this.liveCanvas.width || !this.liveCanvas.height) return;
      const pixel = this.liveCtx.getImageData(0, 0, 1, 1).data;
      if ((pixel[3] ?? 0) === 0) return; // transparente: está tudo certo
    } catch {
      return; // leitura bloqueada: não dá para concluir nada, deixa como está
    }
    this.opaqueLayerDetected = true;
    this.rebuildLiveLayerWithoutLowLatency();
  }

  /** Troca a camada do traço por uma comum. Perde latência, devolve a tela. */
  private rebuildLiveLayerWithoutLowLatency(): void {
    const old = this.liveCanvas;
    const c = document.createElement('canvas');
    c.style.position = 'absolute';
    c.style.inset = '0';
    c.style.zIndex = '2';
    c.style.transformOrigin = '0 0';
    c.style.touchAction = 'none';
    c.style.pointerEvents = 'auto';
    const ctx = c.getContext('2d', { alpha: true });
    if (!ctx) return;
    old.replaceWith(c);
    this.liveCanvas = c;
    this.liveCtx = ctx;
    this.lowLatency = false;
    this.attach(c);
    this.resize();
  }

  /** O que mostrar em Ajustes quando a escrita não estiver bem. */
  getDiagnostics(): { lowLatency: boolean; opaqueLayerDetected: boolean; dpr: number } {
    return { lowLatency: this.lowLatency, opaqueLayerDetected: this.opaqueLayerDetected, dpr: this.dpr };
  }

  // ---------------------------------------------------------------- estado

  setPen(pen: Partial<PenSettings>): void {
    this.pen = { ...this.pen, ...pen };
  }

  getPen(): PenSettings {
    return { ...this.pen };
  }

  setMode(mode: EngineMode): void {
    this.mode = mode;
    // Sair da seleção tem de limpar o que estava selecionado, senão o laço
    // continua "segurando" traços que o usuário já não vê como selecionados.
    if (mode !== 'select') this.setSelection([]);
  }

  getMode(): EngineMode {
    return this.mode;
  }

  setEraser(next: Partial<EraserSettings>): void {
    this.eraser = { ...this.eraser, ...next };
  }

  getEraser(): EraserSettings {
    return { ...this.eraser };
  }

  setSelectMode(mode: SelectSettings['mode']): void {
    this.select = { mode };
    this.setSelection([]);
  }

  getSelectMode(): SelectSettings['mode'] {
    return this.select.mode;
  }

  setOptions(opts: Partial<EngineOptions>): void {
    this.opts = { ...this.opts, ...opts };
  }

  setPaper(paper: Paper): void {
    this.paper = paper;
    this.renderPaper();
  }

  setMarks(marks: Map<number, RowMarkKind>): void {
    this.marks = marks;
    this.renderPaper();
  }

  setHighlight(rows: number[]): void {
    this.highlightRows = rows;
    this.renderPaper();
  }

  load(strokes: Stroke[]): void {
    this.index.clear();
    for (const s of strokes) if (!s.deletedAt) this.index.add(s);
    this.undoStack = [];
    this.redoStack = [];
    this.stats.strokeCount = this.index.size;
    this.renderAll();
  }

  getStrokes(): Stroke[] {
    return this.index.all();
  }

  getStats(): DebugStats {
    return { ...this.stats };
  }

  destroy(): void {
    this.destroyed = true;
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.raf);
    this.container.replaceChildren();
  }

  // ------------------------------------------------------------- viewport

  private resize(): void {
    const rect = this.container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    for (const c of [this.paperCanvas, this.inkCanvas, this.liveCanvas]) {
      c.width = Math.round(rect.width * this.dpr);
      c.height = Math.round(rect.height * this.dpr);
      c.style.width = `${rect.width}px`;
      c.style.height = `${rect.height}px`;
    }
    const previousFit = this.fitScale;
    this.fitScale = rect.width / PAGE_W;
    if (previousFit === 1 && this.viewport.scale === 1) {
      this.viewport = { scale: this.fitScale, tx: 0, ty: 0 };
    } else if (previousFit) {
      this.viewport.scale *= this.fitScale / previousFit;
    }
    this.renderAll();
  }

  private applyTransform(ctx: CanvasRenderingContext2D): void {
    const { scale, tx, ty } = this.viewport;
    ctx.setTransform(scale * this.dpr, 0, 0, scale * this.dpr, tx * this.dpr, ty * this.dpr);
  }

  private clear(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  private screenToPage(x: number, y: number): [number, number] {
    const rect = this.liveCanvas.getBoundingClientRect();
    const { scale, tx, ty } = this.viewport;
    return [(x - rect.left - tx) / scale, (y - rect.top - ty) / scale];
  }

  getViewport(): Viewport {
    return { ...this.viewport };
  }

  setViewport(v: Partial<Viewport>): void {
    this.viewport = { ...this.viewport, ...v };
    this.renderAll();
    this.cb.onViewportChange?.(this.viewport);
  }

  zoomBy(factor: number): void {
    const rect = this.liveCanvas.getBoundingClientRect();
    this.zoomAround(factor, rect.width / 2, rect.height / 2);
  }

  fitWidth(): void {
    this.setViewport({ scale: this.fitScale, tx: 0, ty: this.viewport.ty });
  }

  private zoomAround(factor: number, ox: number, oy: number): void {
    const target = Math.max(this.fitScale * MIN_ZOOM, Math.min(this.fitScale * MAX_ZOOM, this.viewport.scale * factor));
    const k = target / this.viewport.scale;
    this.setViewport({
      scale: target,
      tx: k * (this.viewport.tx - ox) + ox,
      ty: k * (this.viewport.ty - oy) + oy,
    });
  }

  // ------------------------------------------------------------ desenho

  private renderPaper(): void {
    this.clear(this.paperCtx, this.paperCanvas);
    this.applyTransform(this.paperCtx);
    drawPaper(this.paperCtx, { paper: this.paper, marks: this.marks, highlightRows: this.highlightRows });
  }

  private renderInk(): void {
    this.clear(this.inkCtx, this.inkCanvas);
    this.applyTransform(this.inkCtx);
    const selected = new Set(this.selection);
    for (const s of sortForPainting(this.index.all())) {
      paintStroke(this.inkCtx, s);
      if (selected.has(s.id)) {
        this.inkCtx.save();
        this.inkCtx.strokeStyle = '#1F47C9';
        this.inkCtx.lineWidth = 1.5 / this.viewport.scale;
        this.inkCtx.strokeRect(s.bbox[0] - 4, s.bbox[1] - 4, s.bbox[2] - s.bbox[0] + 8, s.bbox[3] - s.bbox[1] + 8);
        this.inkCtx.restore();
      }
    }
    this.stats.strokeCount = this.index.size;
  }

  renderAll(): void {
    const t0 = performance.now();
    this.renderPaper();
    this.renderInk();
    this.stats.lastFrameMs = performance.now() - t0;
  }

  /** Redesenha só o retângulo sujo — usado ao apagar e ao desfazer. */
  private renderRegion(area: BBox): void {
    const pad = 8;
    const box: BBox = [area[0] - pad, area[1] - pad, area[2] + pad, area[3] + pad];
    const ctx = this.inkCtx;
    ctx.save();
    this.applyTransform(ctx);
    ctx.beginPath();
    ctx.rect(box[0], box[1], box[2] - box[0], box[3] - box[1]);
    ctx.clip();
    ctx.clearRect(box[0], box[1], box[2] - box[0], box[3] - box[1]);
    for (const s of sortForPainting(this.index.query(box))) paintStroke(ctx, s);
    ctx.restore();
    this.renderPaper();
  }

  // ------------------------------------------------------------- entrada

  private isPalm(e: PointerEvent): boolean {
    if (e.pointerType !== 'touch') return false;
    if (this.penDown || this.penHovering) return true;
    if (performance.now() - this.penLeftAt < PEN_GRACE_MS) return true;
    return Math.max(e.width, e.height) > this.opts.palmRejectPx;
  }

  /** O botão lateral da S Pen chega como bit 2 em alguns aparelhos e 32 em
   *  outros; tratar os dois evita o "por que a borracha não funciona". */
  private isEraserButton(e: PointerEvent): boolean {
    return (e.buttons & 2) !== 0 || (e.buttons & 32) !== 0 || e.pointerType === 'eraser';
  }

  private canDraw(e: PointerEvent): boolean {
    if (e.pointerType === 'pen' || e.pointerType === 'eraser') return true;
    if (e.pointerType === 'mouse') return true;
    return this.opts.fingerDraw && this.touches.size <= 1;
  }

  /**
   * setPointerCapture lança quando o ponteiro já não está ativo — acontece em
   * toque muito rápido e em evento sintético (teste). Sem captura o traço
   * ainda funciona; com exceção, ele nem começa.
   */
  private capture(el: HTMLElement, pointerId: number): void {
    try { el.setPointerCapture(pointerId); } catch { /* segue sem captura */ }
  }

  private release(el: HTMLElement, pointerId: number): void {
    try { el.releasePointerCapture(pointerId); } catch { /* já solto */ }
  }

  private attach(el: HTMLCanvasElement): void {
    el.addEventListener('contextmenu', (e) => e.preventDefault());

    el.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'pen') this.penHovering = true;
    });
    el.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'pen') {
        this.penHovering = false;
        this.penLeftAt = performance.now();
      }
    });

    el.addEventListener('pointerdown', (e) => this.onDown(e), { passive: false });
    el.addEventListener('pointermove', (e) => this.onMove(e), { passive: false });
    el.addEventListener('pointerup', (e) => this.onUp(e));
    el.addEventListener('pointercancel', (e) => this.onUp(e));

    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      if (e.ctrlKey) {
        // A pinça do trackpad chega como wheel + ctrl.
        this.zoomAround(e.deltaY < 0 ? 1.1 : 0.9, e.clientX - rect.left, e.clientY - rect.top);
        return;
      }
      // No notebook não há dedo para rolar: a roda faz o papel dele.
      this.viewport = { ...this.viewport, tx: this.viewport.tx - e.deltaX, ty: this.viewport.ty - e.deltaY };
      this.clampViewport();
      this.renderAll();
      this.cb.onViewportChange?.(this.viewport);
    }, { passive: false });
  }

  private onDown(e: PointerEvent): void {
    if (this.destroyed) return;
    if (e.pointerType === 'touch') {
      if (this.isPalm(e)) return;
      this.touches.set(e.pointerId, { id: e.pointerId, x: e.clientX, y: e.clientY });
      if (this.touches.size === 2) this.startGesture();
      if (this.touches.size >= 1 && !this.opts.fingerDraw) {
        this.capture(this.liveCanvas, e.pointerId);
        return;
      }
    }

    if (!this.canDraw(e)) return;
    e.preventDefault();
    this.capture(this.liveCanvas, e.pointerId);
    if (e.pointerType === 'pen') this.penDown = true;

    const [x, y] = this.screenToPage(e.clientX, e.clientY);
    // O botão lateral da S Pen vira borracha em qualquer modo, como no
    // Samsung Notes, sem trocar a ferramenta escolhida.
    const mode: EngineMode = this.isEraserButton(e) ? 'eraser' : this.mode;

    if (mode === 'eraser') {
      this.erasing = { id: e.pointerId, last: [x, y], originals: new Map(), masks: new Map() };
      this.eraseAt(x, y, x, y);
      return;
    }
    if (mode === 'select') {
      const inside = this.selection.length > 0 && this.selectionBox() && this.pointInSelection(x, y);
      if (inside) {
        this.dragging = { id: e.pointerId, from: [x, y], dx: 0, dy: 0 };
      } else {
        this.lasso = { id: e.pointerId, poly: [x, y], rect: this.select.mode === 'rect' };
        this.setSelection([]);
      }
      return;
    }

    this.drawing = {
      id: e.pointerId,
      pts: [x, y, this.pressureOf(e), 0],
      startedAt: performance.now(),
      pen: { ...this.pen },
    };
    this.lastEventTs = e.timeStamp;
    this.scheduleLiveDraw();
  }

  private pressureOf(e: PointerEvent): number {
    if (e.pointerType === 'mouse') return 0.5;
    return Math.max(0.05, Math.min(1, e.pressure || 0.5));
  }

  private onMove(e: PointerEvent): void {
    if (this.destroyed) return;

    if (e.pointerType === 'touch' && this.touches.has(e.pointerId)) {
      const t = this.touches.get(e.pointerId);
      if (t) { t.x = e.clientX; t.y = e.clientY; }
      if (!this.drawing) {
        e.preventDefault();
        this.updateGesture();
        return;
      }
    }

    if (this.drawing?.id === e.pointerId) {
      e.preventDefault();
      // getCoalescedEvents: a S Pen produz mais pontos do que quadros de tela.
      // Sem isso, letra pequena e rápida perde curvas (SPEC §7.1).
      const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
      const t0 = this.drawing.startedAt;
      for (const ev of events.length ? events : [e]) {
        const [x, y] = this.screenToPage(ev.clientX, ev.clientY);
        this.drawing.pts.push(x, y, this.pressureOf(ev), Math.round(performance.now() - t0));
      }
      this.lastEventTs = e.timeStamp;
      this.scheduleLiveDraw(e);
      return;
    }

    if (this.erasing?.id === e.pointerId) {
      e.preventDefault();
      const [x, y] = this.screenToPage(e.clientX, e.clientY);
      const [lx, ly] = this.erasing.last;
      this.eraseAt(lx, ly, x, y);
      this.erasing.last = [x, y];
      if (this.eraser.mode === 'area') this.scheduleLiveDraw();
      return;
    }

    if (this.lasso?.id === e.pointerId) {
      e.preventDefault();
      const [x, y] = this.screenToPage(e.clientX, e.clientY);
      if (this.lasso.rect) {
        this.lasso.poly = [this.lasso.poly[0] as number, this.lasso.poly[1] as number, x, y];
      } else {
        this.lasso.poly.push(x, y);
      }
      this.scheduleLiveDraw();
      return;
    }

    if (this.dragging?.id === e.pointerId) {
      e.preventDefault();
      const [x, y] = this.screenToPage(e.clientX, e.clientY);
      this.dragging.dx = x - this.dragging.from[0];
      this.dragging.dy = y - this.dragging.from[1];
      this.scheduleLiveDraw();
    }
  }

  private onUp(e: PointerEvent): void {
    if (this.destroyed) return;
    this.release(this.liveCanvas, e.pointerId);

    if (e.pointerType === 'touch') {
      this.touches.delete(e.pointerId);
      if (this.touches.size < 2 && this.gesture) this.endGesture();
    }
    if (e.pointerType === 'pen') {
      this.penDown = false;
      this.penLeftAt = performance.now();
    }

    if (this.drawing?.id === e.pointerId) {
      this.commitDrawing();
      return;
    }
    if (this.erasing?.id === e.pointerId) {
      this.commitAreaErase();
      this.erasing = null;
      this.clear(this.liveCtx, this.liveCanvas);
      return;
    }
    if (this.lasso?.id === e.pointerId) {
      const { poly, rect } = this.lasso;
      this.lasso = null;
      this.clear(this.liveCtx, this.liveCanvas);
      if (rect && poly.length === 4) {
        const box: BBox = [
          Math.min(poly[0] as number, poly[2] as number), Math.min(poly[1] as number, poly[3] as number),
          Math.max(poly[0] as number, poly[2] as number), Math.max(poly[1] as number, poly[3] as number),
        ];
        this.setSelection(this.index.query(box).filter((s) => strokeInRect(s, box)).map((s) => s.id));
      } else if (!rect && poly.length >= 6) {
        this.setSelection(this.index.all().filter((s) => strokeInLasso(s, poly)).map((s) => s.id));
      }
      return;
    }
    if (this.dragging?.id === e.pointerId) {
      const { dx, dy } = this.dragging;
      this.dragging = null;
      this.clear(this.liveCtx, this.liveCanvas);
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) this.moveSelection(dx, dy);
      else this.renderInk();
    }
  }

  // --------------------------------------------------------------- gestos

  private startGesture(): void {
    const pts = [...this.touches.values()];
    if (pts.length < 2) return;
    const [a, b] = pts as [TouchPoint, TouchPoint];
    const rect = this.liveCanvas.getBoundingClientRect();
    this.gesture = {
      startViewport: { ...this.viewport },
      originX: (a.x + b.x) / 2 - rect.left,
      originY: (a.y + b.y) / 2 - rect.top,
      startDist: Math.hypot(a.x - b.x, a.y - b.y),
      startCx: (a.x + b.x) / 2,
      startCy: (a.y + b.y) / 2,
    };
  }

  private updateGesture(): void {
    const pts = [...this.touches.values()];
    if (!pts.length) return;

    if (pts.length === 1 && !this.gesture) {
      const t = pts[0] as TouchPoint;
      if (!this.panStart) this.panStart = { x: t.x, y: t.y, vp: { ...this.viewport } };
      // Um dedo rola a página (SPEC §7.1). Usa transform de CSS durante o
      // gesto: o compositor cuida, e nada é redesenhado a 120 Hz.
      this.previewTransform(t.x - this.panStart.x, t.y - this.panStart.y, 1, 0, 0);
      return;
    }

    if (pts.length >= 2) {
      if (!this.gesture) this.startGesture();
      const g = this.gesture;
      if (!g) return;
      const [a, b] = pts as [TouchPoint, TouchPoint];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      const raw = g.startDist > 0 ? dist / g.startDist : 1;
      const target = Math.max(this.fitScale * MIN_ZOOM, Math.min(this.fitScale * MAX_ZOOM, g.startViewport.scale * raw));
      const k = target / g.startViewport.scale;
      this.previewTransform(cx - g.startCx, cy - g.startCy, k, g.originX, g.originY);
    }
  }

  private panStart: { x: number; y: number; vp: Viewport } | null = null;

  private previewTransform(dx: number, dy: number, k: number, ox: number, oy: number): void {
    const t = `translate(${dx}px, ${dy}px) translate(${ox}px, ${oy}px) scale(${k}) translate(${-ox}px, ${-oy}px)`;
    this.paperCanvas.style.transform = t;
    this.inkCanvas.style.transform = t;
    this.pendingTransform = { dx, dy, k, ox, oy };
  }

  private pendingTransform: { dx: number; dy: number; k: number; ox: number; oy: number } | null = null;

  private endGesture(): void {
    const base = this.gesture?.startViewport ?? this.panStart?.vp ?? this.viewport;
    const p = this.pendingTransform;
    this.gesture = null;
    this.panStart = null;
    this.pendingTransform = null;
    this.paperCanvas.style.transform = '';
    this.inkCanvas.style.transform = '';
    if (!p) return;
    // Ao soltar, o transform vira viewport de verdade e tudo é redesenhado
    // nítido — durante o gesto era o bitmap escalado.
    this.viewport = {
      scale: base.scale * p.k,
      tx: p.k * (base.tx - p.ox) + p.ox + p.dx,
      ty: p.k * (base.ty - p.oy) + p.oy + p.dy,
    };
    this.clampViewport();
    this.renderAll();
    this.cb.onViewportChange?.(this.viewport);
  }

  private clampViewport(): void {
    const rect = this.liveCanvas.getBoundingClientRect();
    const pageW = PAGE_W * this.viewport.scale;
    const pageH = PAGE_H * this.viewport.scale;
    const minTx = Math.min(0, rect.width - pageW);
    const maxTx = Math.max(0, rect.width - pageW);
    const minTy = Math.min(0, rect.height - pageH);
    this.viewport.tx = Math.max(minTx, Math.min(maxTx, this.viewport.tx));
    this.viewport.ty = Math.max(minTy, Math.min(0, this.viewport.ty));
  }

  // ------------------------------------------------------------ traço vivo

  private scheduleLiveDraw(e?: PointerEvent): void {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      const t0 = performance.now();
      this.drawLive(e);
      const dt = performance.now() - t0;
      this.stats.lastFrameMs = dt;
      this.frameTimes.push(t0);
      while (this.frameTimes.length > 60) this.frameTimes.shift();
      const first = this.frameTimes[0];
      if (this.frameTimes.length > 5 && first !== undefined) {
        this.stats.fps = Math.round(((this.frameTimes.length - 1) * 1000) / (t0 - first));
      }
      if (this.lastEventTs) this.stats.latencyMs = Math.max(0, Math.round(performance.now() - this.lastEventTs));
    });
  }

  private drawLive(e?: PointerEvent): void {
    this.clear(this.liveCtx, this.liveCanvas);
    this.applyTransform(this.liveCtx);

    if (this.drawing) {
      const pts = pointsToTriples(new Float32Array(this.drawing.pts));
      // Eventos previstos só na ponta provisória: eles NUNCA entram no traço
      // salvo, senão o caderno guarda posições que a caneta não visitou.
      if (e && typeof e.getPredictedEvents === 'function') {
        for (const pe of e.getPredictedEvents()) {
          const [x, y] = this.screenToPage(pe.clientX, pe.clientY);
          pts.push([x, y, this.pressureOf(pe)]);
        }
      }
      const pen = this.drawing.pen;
      const profile = profileOf(pen.kind);
      const path = buildPathFor(profile, pts, pen.size, false);
      this.liveCtx.save();
      if (profile.behindInk) this.liveCtx.globalCompositeOperation = 'multiply';
      this.liveCtx.globalAlpha = pen.opacity;
      this.liveCtx.fillStyle = pen.color;
      this.liveCtx.fill(path);
      this.liveCtx.restore();
    }

    if (this.lasso && this.lasso.poly.length >= 4) {
      const ctx = this.liveCtx;
      const poly = this.lasso.poly;
      ctx.save();
      ctx.strokeStyle = '#1F47C9';
      ctx.setLineDash([8 / this.viewport.scale, 6 / this.viewport.scale]);
      ctx.lineWidth = 1.5 / this.viewport.scale;
      ctx.beginPath();
      if (this.lasso.rect) {
        ctx.rect(
          poly[0] as number, poly[1] as number,
          (poly[2] as number) - (poly[0] as number),
          (poly[3] as number) - (poly[1] as number),
        );
      } else {
        ctx.moveTo(poly[0] as number, poly[1] as number);
        for (let i = 2; i < poly.length; i += 2) ctx.lineTo(poly[i] as number, poly[i + 1] as number);
      }
      ctx.stroke();
      ctx.restore();
    }

    if (this.erasing && this.eraser.mode === 'area') {
      const ctx = this.liveCtx;
      // Prévia: os pedaços que vão sobrar, redesenhados a cada quadro.
      for (const [id, original] of this.erasing.originals) {
        const mask = this.erasing.masks.get(id) as Uint8Array;
        for (const piece of piecesFromMask(original.points, mask)) {
          const bbox = bboxOfPoints(piece);
          paintStroke(ctx, { ...original, id: `${id}~preview`, points: piece, bbox });
        }
      }
      const [ex, ey] = this.erasing.last;
      ctx.save();
      ctx.strokeStyle = '#C0352B';
      ctx.lineWidth = 1.5 / this.viewport.scale;
      ctx.beginPath();
      ctx.arc(ex, ey, this.eraser.size / 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    if (this.dragging && this.selection.length) {
      const ctx = this.liveCtx;
      ctx.save();
      ctx.translate(this.dragging.dx, this.dragging.dy);
      for (const id of this.selection) {
        const s = this.index.get(id);
        if (s) paintStroke(ctx, s);
      }
      ctx.restore();
    }
  }

  private commitDrawing(): void {
    const d = this.drawing;
    this.drawing = null;
    if (!d) return;
    this.clear(this.liveCtx, this.liveCanvas);
    if (d.pts.length < POINT_STRIDE * 2) {
      // Um toque só: ponto. Ainda é escrita (pingo do i, ponto final).
      d.pts.push(...d.pts.slice(0, POINT_STRIDE));
    }

    const points = new Float32Array(d.pts);
    const bbox = bboxOfPoints(points);
    const stroke: Stroke = {
      id: uuidv7(),
      pageId: '',
      tool: d.pen.kind,
      color: d.pen.color,
      size: d.pen.size,
      opacity: d.pen.opacity,
      points,
      bbox,
      row: rowOfBBox(bbox),
      createdAt: Date.now(),
    };

    this.index.add(stroke);
    this.pushUndo({ type: 'add', strokes: [stroke] });
    // "Carimba" só o traço novo na camada dos finalizados, sem redesenhar a
    // página inteira — é o que mantém a escrita fluida com a página cheia.
    this.applyTransform(this.inkCtx);
    paintStroke(this.inkCtx, stroke);
    this.stats.strokeCount = this.index.size;
    this.cb.onStrokeCommitted(stroke);
  }

  // ---------------------------------------------------------------- ações

  private eraseAt(ax: number, ay: number, bx: number, by: number): void {
    const radius = this.eraser.mode === 'area' ? this.eraser.size / 2 : 6 / this.viewport.scale;
    const area: BBox = [Math.min(ax, bx), Math.min(ay, by), Math.max(ax, bx), Math.max(ay, by)];
    let hits = this.index
      .query(area, radius + 24)
      .filter((s) => strokeHitBySegment(s, ax, ay, bx, by, radius));
    if (this.eraser.highlighterOnly) hits = hits.filter((s) => isHighlighter(s.tool));
    if (!hits.length) return;

    if (this.eraser.mode === 'stroke') {
      this.deleteStrokes(hits, true);
      return;
    }

    // Por área: durante o gesto NADA é gravado. O traço tocado sai do índice,
    // vai inteiro para `originals`, e o que a borracha come é marcado numa
    // máscara que só cresce. Gravar a cada movimento — como esta função fazia
    // antes — repartia o traço em pedaços cada vez menores a cada evento da
    // caneta, até sobrar nada: uma passada apagava a página inteira e enchia a
    // fila de sincronização com centenas de traços intermediários.
    const g = this.erasing;
    if (!g) return;
    for (const s of hits) {
      if (!g.originals.has(s.id)) {
        g.originals.set(s.id, s);
        g.masks.set(s.id, new Uint8Array(s.points.length / POINT_STRIDE));
        this.index.remove(s.id);
      }
      const original = g.originals.get(s.id) as Stroke;
      const mask = g.masks.get(s.id) as Uint8Array;
      markErasedPoints(original.points, mask, ax, ay, bx, by, radius + original.size / 2);
    }
    this.renderInk();
  }

  /** Fecha o gesto da borracha por área: uma gravação, um desfazer. */
  private commitAreaErase(): void {
    const g = this.erasing;
    if (!g || !g.originals.size) return;

    const removed: Stroke[] = [];
    const created: Stroke[] = [];
    for (const [id, original] of g.originals) {
      const mask = g.masks.get(id) as Uint8Array;
      const pieces = piecesFromMask(original.points, mask);
      removed.push(original);
      for (const piece of pieces) {
        const bbox = bboxOfPoints(piece);
        created.push({
          ...original, id: uuidv7(), points: piece, bbox,
          row: rowOfBBox(bbox), createdAt: Date.now(),
        });
      }
    }
    for (const s of created) this.index.add(s);
    for (const s of removed) forgetStroke(s.id);

    this.pushUndo({ type: 'delete', strokes: removed });
    this.undoStack.push({ type: 'add', strokes: created });
    this.cb.onStrokesDeleted(removed.map((s) => s.id));
    for (const s of created) this.cb.onStrokeCommitted(s);
    this.stats.strokeCount = this.index.size;
    this.renderAll();
  }

  private deleteStrokes(strokes: Stroke[], record: boolean): void {
    if (!strokes.length) return;
    let box: BBox = [...strokes[0]!.bbox];
    for (const s of strokes) {
      this.index.remove(s.id);
      forgetStroke(s.id);
      box = [
        Math.min(box[0], s.bbox[0]), Math.min(box[1], s.bbox[1]),
        Math.max(box[2], s.bbox[2]), Math.max(box[3], s.bbox[3]),
      ];
    }
    if (record) this.pushUndo({ type: 'delete', strokes });
    this.renderRegion(box);
    this.stats.strokeCount = this.index.size;
    this.cb.onStrokesDeleted(strokes.map((s) => s.id));
  }

  /** "Apagar tudo" da borracha. */
  eraseAll(): void {
    const all = this.index.all();
    if (!all.length) return;
    this.deleteStrokes(all, true);
  }

  /** Troca a cor dos traços selecionados (traço é imutável: recria). */
  recolorSelection(color: string): void {
    const old = this.selection.map((id) => this.index.get(id)).filter((s): s is Stroke => !!s);
    if (!old.length) return;
    const created = old.map((s) => ({ ...s, id: uuidv7(), color, createdAt: Date.now() }));
    for (const s of old) { this.index.remove(s.id); forgetStroke(s.id); }
    for (const s of created) this.index.add(s);
    this.pushUndo({ type: 'delete', strokes: old });
    this.undoStack.push({ type: 'add', strokes: created });
    this.setSelection(created.map((s) => s.id));
    this.renderInk();
    this.cb.onStrokesDeleted(old.map((s) => s.id));
    for (const s of created) this.cb.onStrokeCommitted(s);
  }

  deleteSelection(): void {
    const strokes = this.selection.map((id) => this.index.get(id)).filter((s): s is Stroke => !!s);
    this.setSelection([]);
    this.deleteStrokes(strokes, true);
  }

  /** Mover é apagar e criar: traço não muda de lugar, ele é substituído. */
  private moveSelection(dx: number, dy: number): void {
    const old = this.selection.map((id) => this.index.get(id)).filter((s): s is Stroke => !!s);
    if (!old.length) return;
    const created: Stroke[] = old.map((s) => {
      const points = translatePoints(s.points, dx, dy);
      const bbox = bboxOfPoints(points);
      return { ...s, id: uuidv7(), points, bbox, row: rowOfBBox(bbox), createdAt: Date.now() };
    });
    for (const s of old) { this.index.remove(s.id); forgetStroke(s.id); }
    for (const s of created) this.index.add(s);
    this.pushUndo({ type: 'delete', strokes: old });
    this.undoStack.push({ type: 'add', strokes: created });
    this.setSelection(created.map((s) => s.id));
    this.renderInk();
    this.cb.onStrokesDeleted(old.map((s) => s.id));
    for (const s of created) this.cb.onStrokeCommitted(s);
  }

  setSelection(ids: string[]): void {
    this.selection = ids;
    this.cb.onSelectionChange?.(ids);
    this.renderInk();
  }

  private selectionBox(): BBox | null {
    const strokes = this.selection.map((id) => this.index.get(id)).filter((s): s is Stroke => !!s);
    if (!strokes.length) return null;
    let box: BBox = [...strokes[0]!.bbox];
    for (const s of strokes) {
      box = [Math.min(box[0], s.bbox[0]), Math.min(box[1], s.bbox[1]), Math.max(box[2], s.bbox[2]), Math.max(box[3], s.bbox[3])];
    }
    return box;
  }

  private pointInSelection(x: number, y: number): boolean {
    const b = this.selectionBox();
    return !!b && x >= b[0] - 10 && x <= b[2] + 10 && y >= b[1] - 10 && y <= b[3] + 10;
  }

  // ------------------------------------------------------- desfazer/refazer

  private pushUndo(action: UndoAction): void {
    this.undoStack.push(action);
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
    this.redoStack = [];
  }

  canUndo(): boolean { return this.undoStack.length > 0; }
  canRedo(): boolean { return this.redoStack.length > 0; }

  undo(): void {
    const action = this.undoStack.pop();
    if (!action) return;
    this.invert(action);
    this.redoStack.push(action);
  }

  redo(): void {
    const action = this.redoStack.pop();
    if (!action) return;
    this.invert(action, true);
    this.undoStack.push(action);
  }

  private invert(action: UndoAction, forward = false): void {
    const adding = forward ? action.type === 'add' : action.type === 'delete';
    if (adding) {
      for (const s of action.strokes) this.index.add(s);
      for (const s of action.strokes) this.cb.onStrokeCommitted(s);
    } else {
      for (const s of action.strokes) { this.index.remove(s.id); forgetStroke(s.id); }
      this.cb.onStrokesDeleted(action.strokes.map((s) => s.id));
    }
    this.stats.strokeCount = this.index.size;
    this.renderInk();
  }

  /** Path2D do traço, para quem precisa desenhar fora do motor (miniatura). */
  pathOf(s: Stroke): Path2D {
    return pathForStroke(s);
  }
}
